import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import StatusBadge from '../components/StatusBadge'
import './ReviewerProfile.css'

export default function ReviewerProfile() {
  const { reviewerUsername } = useParams()
  const [reviewer, setReviewer] = useState(null)
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [verifiedCount, setVerifiedCount] = useState(0)

  useEffect(() => { fetchReviewer() }, [reviewerUsername])

  async function fetchReviewer() {
    setLoading(true)
    const { data: rev } = await supabase
      .from('profiles')
      .select('*')
      .eq('reviewer_username', reviewerUsername)
      .eq('is_admin', true)
      .single()

    if (!rev) { setNotFound(true); setLoading(false); return }
    setReviewer(rev)

    // Fetch public verified projects they reviewed
    const { data: projs, count } = await supabase
      .from('projects')
      .select('*, profiles!projects_user_id_fkey(full_name, display_name, username)', { count: 'exact' })
      .eq('verified_by_id', rev.id)
      .eq('status', 'verified')
      .eq('visibility', 'public')
      .order('verified_at', { ascending: false })
      .limit(30)

    setProjects(projs || [])
    setVerifiedCount(count || 0)
    setLoading(false)
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  if (notFound) return (
    <div className="container" style={{ textAlign: 'center', padding: 80 }}>
      <h2>Reviewer not found</h2>
      <p style={{ color: 'var(--text-2)', marginTop: 8 }}>This reviewer profile doesn't exist.</p>
      <Link to="/browse" className="btn btn-secondary" style={{ marginTop: 24 }}>Browse verified work</Link>
    </div>
  )

  const displayName = reviewer.reviewer_title
    ? `${reviewer.display_name || reviewer.full_name} · ${reviewer.reviewer_title}`
    : reviewer.display_name || reviewer.full_name

  return (
    <div className="reviewer-page page-fade">
      <div className="container">
        <div className="reviewer-header card">
          <div className="reviewer-header-left">
            <div className="reviewer-avatar-wrap">
              {reviewer.reviewer_avatar_url
                ? <img src={reviewer.reviewer_avatar_url} alt={displayName} className="reviewer-avatar-img" />
                : <div className="reviewer-avatar-placeholder">{(reviewer.display_name || reviewer.full_name || 'R').charAt(0).toUpperCase()}</div>
              }
            </div>
            <div>
              <div className="reviewer-name-row">
                <h1>{reviewer.display_name || reviewer.full_name}</h1>
                <span className="reviewer-team-badge">HumanOrigin Review Team</span>
              </div>
              {reviewer.reviewer_title && <div className="reviewer-title">{reviewer.reviewer_title}</div>}
              {reviewer.reviewer_bio && <p className="reviewer-bio">{reviewer.reviewer_bio}</p>}
              <div className="reviewer-stat">
                <span className="reviewer-stat-num">{verifiedCount}</span> work{verifiedCount !== 1 ? 's' : ''} verified
              </div>
            </div>
          </div>
        </div>

        <div className="reviewer-trust-note">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <circle cx="7.5" cy="7.5" r="6.5" stroke="#c8f55a" strokeWidth="1.2"/>
            <path d="M4.5 7.5l2 2 4-4" stroke="#c8f55a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          This reviewer is a verified human member of the HumanOrigin team. Their name appears on every certificate they approve.
        </div>

        {projects.length > 0 && (
          <div className="reviewer-projects-section">
            <h2>Verified by {reviewer.display_name || reviewer.full_name} <span className="profile-section-count">{verifiedCount}</span></h2>
            <div className="reviewer-projects-grid">
              {projects.map(p => <ReviewerProjectCard key={p.id} project={p} />)}
            </div>
          </div>
        )}

        {projects.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-2)' }}>
            <p>No publicly visible verified works yet.</p>
          </div>
        )}
      </div>
    </div>
  )
}

function ReviewerProjectCard({ project }) {
  const creator = project.profiles
  const creatorName = creator?.display_name || creator?.full_name || 'Anonymous'
  return (
    <Link to={`/project/${project.id}`} className="profile-project-card card">
      <div className="ppc-top">
        <span className="ppc-category">{project.category}</span>
        <StatusBadge status={project.status} />
      </div>
      <h3 className="ppc-title">{project.title}</h3>
      <p className="ppc-desc" style={{ fontSize: 12, color: 'var(--text-3)' }}>
        by {creator?.username ? <Link to={`/u/${creator.username}`}>{creatorName}</Link> : creatorName}
      </p>
      <div className="ppc-footer">
        <span className="ppc-date">{new Date(project.verified_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        <span className="ppc-verified">✓ Verified</span>
      </div>
    </Link>
  )
}
